// Stacks simultaneous pending approvals into an accordion: the active
// approval renders its full card while every other pending approval
// collapses to a one-line row the reader can expand. The harness owns which
// approval is active; each slot only knows its own owner id. Without a
// provider (read-only surfaces, galleries) every slot renders expanded, so
// the stack is purely additive.
import { ChevronDown } from 'lucide-react';
import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useRef,
} from 'react';

import { CardResize } from '@apps/web/features/phoebe_chat_v3/ui/primitives/card_transition';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

export type ApprovalStack = Readonly<{
    /** Owner occurrence ids of pending approvals, in transcript order. */
    readonly pendingOwnerIds: ReadonlyArray<string>;
    readonly activeOwnerId: string | null;
    readonly activate: (ownerId: string) => void;
}>;

export const ApprovalStackContext = createContext<ApprovalStack | null>(null);

function CollapsedApprovalRow({
    title,
    onExpand,
}: Readonly<{
    readonly title: string;
    readonly onExpand: () => void;
}>): ReactNode {
    return (
        <button
            type="button"
            aria-expanded={false}
            onClick={onExpand}
            className="bg-card hover:bg-accent focus-visible:ring-ring/50 flex w-full min-w-0 items-center gap-2.5 rounded-lg border px-4 py-2.5 text-left transition-colors duration-150 outline-none focus-visible:ring-[3px] motion-reduce:transition-none"
        >
            <span className="min-w-0 flex-1">
                <span className="text-v3-chat block truncate font-semibold tracking-[-0.01em]">
                    <ResolvedReferenceText text={title} />
                </span>
                <span className="text-muted-foreground text-v3-chat-meta mt-0.5 flex items-center gap-1.5">
                    <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-full bg-amber-500"
                    />
                    Waiting for approval
                </span>
            </span>
            <ChevronDown
                aria-hidden="true"
                className="text-muted-foreground size-4 shrink-0"
            />
        </button>
    );
}

/**
 * Wraps one approval's card. While two or more approvals are pending, only
 * the active slot shows its card; the rest show the collapsed row. Both
 * states stay mounted — a coordinator's in-progress message edits must
 * survive expanding a different approval and coming back.
 */
export function ApprovalStackSlot({
    ownerId,
    title,
    children,
}: Readonly<{
    readonly ownerId: string;
    readonly title: string;
    readonly children: ReactNode;
}>): ReactNode {
    const stack = useContext(ApprovalStackContext);
    const stacked =
        stack !== null &&
        stack.pendingOwnerIds.length > 1 &&
        stack.pendingOwnerIds.includes(ownerId);
    const collapsed = stacked && stack.activeOwnerId !== ownerId;
    const expandedRef = useRef<HTMLDivElement | null>(null);
    // Expanding removes the clicked row from the page; without a focus
    // handoff, keyboard focus falls to <body> and the reader's tab position
    // is lost. Auto-advance after a decision deliberately does not steal
    // focus — the reader may be typing elsewhere.
    const focusOnExpandRef = useRef(false);
    useEffect(() => {
        if (!collapsed && focusOnExpandRef.current) {
            focusOnExpandRef.current = false;
            expandedRef.current?.focus();
        }
    }, [collapsed]);
    return (
        <CardResize>
            {collapsed && stack !== null && (
                <div className="animate-in fade-in-0 duration-300 motion-reduce:animate-none">
                    <CollapsedApprovalRow
                        title={title}
                        onExpand={() => {
                            focusOnExpandRef.current = true;
                            stack.activate(ownerId);
                        }}
                    />
                </div>
            )}
            {/* The card hides via display (not unmounting) so a
                coordinator's in-progress edits survive collapsing; the fade
                re-runs each time it becomes visible again. */}
            <div
                ref={expandedRef}
                tabIndex={-1}
                hidden={collapsed}
                className="outline-none"
            >
                {children}
            </div>
        </CardResize>
    );
}
